import type { ToOptions } from "@tanstack/react-router";
import { useMemo } from "react";
import type { Key } from "react-aria";
import type { TabsProps as RATabsProps } from "react-aria-components";
import {
  Tab as RATab,
  TabList as RATabList,
  TabPanel as RATabPanel,
  Tabs as RATabs,
} from "react-aria-components";
import type { VariantProps } from "tailwind-variants";
import { tv } from "tailwind-variants";

import Chip from "./Chip";
import type { ExtractValues } from "./types";

export interface TabsItem<K extends Key> {
  label: string;
  value: K;
  count?: number;
  children?: React.ReactNode | React.ReactNode[] | (() => React.ReactNode);
  href?: ToOptions;
}

const tabs = tv({
  base: "",
  slots: {
    list: "flex w-full flex-row",
    item: `
      group/item cursor-pointer text-sm break-keep whitespace-nowrap
      transition-colors duration-300
    `,
    panel: "",
    "list-wrapper": "",
  },
  variants: {
    variant: {
      underlined: {
        list: "gap-4",
        "list-wrapper": "mb-4 border-b border-border pt-1",
        item: `
          relative flex flex-col items-center px-1 pb-3 leading-5 font-normal
          text-text-tertiary
          after:absolute after:bottom-0 after:left-0 after:h-1 after:w-full
          after:rounded-full after:bg-primary
          after:opacity-0 after:transition-opacity after:duration-300
          data-[selected=true]:font-medium
          data-[selected=true]:text-text-primary
          data-[selected=true]:after:opacity-100
        `,
      },
      "agbw-subtabs": {
        list: "flex-wrap gap-x-7 gap-y-1",
        "list-wrapper": "mb-4 pt-3 pb-1",
        item: `
          border-b-2 border-border px-0.5
          py-2 text-text-tertiary
          transition-colors duration-300
          hover:border-border hover:text-text-primary
          data-[selected=true]:border-primary
          data-[selected=true]:font-medium
          data-[selected=true]:text-text-primary
        `,
      },
      default: {
        item: `
          flex h-10 min-w-36 items-center gap-2 rounded-md px-6
          text-text-primary
          data-[selected=true]:bg-primary/20
        `,
        list: `items-center rounded-lg bg-secondary`,
        "list-wrapper": "rounded-lg bg-secondary p-2.5 shadow-tabs",
      },
    },
  },
  defaultVariants: {
    variant: "underlined",
  },
});

export interface TabsProps<K extends Key, T extends TabsItem<K>> extends Omit<
  RATabsProps,
  "children" | "onSelectionChange" | "items" | "defaultSelectedKey"
> {
  readonly items: T[] | readonly T[];
  readonly selectedKey?: ExtractValues<T[]>;
  readonly onSelectionChange?: (key: ExtractValues<T[]> | null) => void;
  readonly className?: string;
  readonly defaultSelectedKey?: ExtractValues<T[]>;
  readonly classNames?: {
    list?: string;
    item?: string;
    panel?: string;
    listWrapper?: string;
  };
  readonly children?: React.ReactNode;
}

export default function Tabs<K extends Key, T extends TabsItem<K>>({
  onSelectionChange,
  className,
  classNames,
  items,
  variant,
  children,
  ...props
}: TabsProps<K, T> & VariantProps<typeof tabs>) {
  const { list, item, panel, "list-wrapper": listWrapper } = tabs({ variant });

  const handleSelectionChange = useMemo(
    () =>
      onSelectionChange === undefined
        ? undefined
        : (key: Key | null) =>
            onSelectionChange(key as ExtractValues<T[]> | null),
    [onSelectionChange],
  );

  const tabsComponents = items.map((i) => (
    <RATab
      key={i.value}
      id={`${i.value}`}
      href={i.href}
      className={item({ className: classNames?.item })}
    >
      <div className="mx-auto flex items-center justify-center gap-2">
        <span>{i.label}</span>
        {i.count !== undefined && i.count !== null && (
          <Chip
            rounded="full"
            color="tertiary"
            size="sm"
            className={`border-border group-data-[selected=true]/item:border-primary/50 group-data-[selected=true]/item:bg-primary/20 size-6 border p-0 font-medium`}
          >
            {i.count}
          </Chip>
        )}
      </div>
    </RATab>
  ));

  const panelsComponents = children ? (
    <RATabPanel
      id={props.selectedKey}
      className={panel({ className: classNames?.panel })}
    >
      {children}
    </RATabPanel>
  ) : (
    items.map((tabItem) => (
      <RATabPanel
        key={tabItem.value}
        id={`${tabItem.value}`}
        className={panel({ className: classNames?.panel })}
      >
        {typeof tabItem.children === "function"
          ? tabItem.children()
          : tabItem.children}
      </RATabPanel>
    ))
  );

  return (
    <RATabs
      {...props}
      onSelectionChange={handleSelectionChange}
      className={className}
    >
      <div className={listWrapper({ className: classNames?.listWrapper })}>
        <RATabList className={list({ className: classNames?.list })}>
          {tabsComponents}
        </RATabList>
      </div>
      {panelsComponents}
    </RATabs>
  );
}
